import { useState } from 'react';

import { Check, ChevronDown } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { buttonVariants } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { formatAgentModelPrice, getAgentModelName } from '../lib/agentModels';
import type { AgentModelOptionsResponse } from '../services/projects.api';
import { ModelTags } from './AgentModelField';

export interface ChangeModelPopoverProps {
  modelOptions: AgentModelOptionsResponse;
  /** The version Demo Chat uses. Null disables the button: there is no version to test a model on. */
  latestVersion: { version: number; agentAIModel: string; isModelTest: boolean } | null;
  isSaving: boolean;
  onSelect: (modelId: string) => Promise<void>;
}

/** Changes the model of the latest version in place. One click saves; real clients keep the production model. */
const ChangeModelPopover = ({
  modelOptions,
  latestVersion,
  isSaving,
  onSelect,
}: ChangeModelPopoverProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { options, currentModel } = modelOptions;
  const currentOption = options.find((option) => option.id === currentModel);
  const selectedModel = modelOptions.demoChatModel;
  // On a subproject with no model test of its own, Demo Chat runs the master's test.
  const isMasterTest = !!latestVersion && !latestVersion.isModelTest && selectedModel !== currentModel;

  const handleSelect = async (modelId: string): Promise<void> => {
    if (modelId === selectedModel) {
      setOpen(false);
      return;
    }
    setError(null);
    try {
      await onSelect(modelId);
      setOpen(false);
    } catch (e) {
      setError(extractErrorMessage(e, 'No se pudo cambiar el modelo.'));
    }
  };

  if (!latestVersion) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <button type="button" disabled className={buttonVariants({ variant: 'outline', size: 'sm' })}>
              Cambiar modelo
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">Crea una versión Latest para probar otro modelo</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setError(null);
      }}
    >
      <PopoverTrigger className={buttonVariants({ variant: 'outline', size: 'sm' })}>
        Cambiar modelo
        <ChevronDown data-icon="inline-end" />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] gap-0 p-0">
        <PopoverHeader className="border-b border-border-subtle px-4 py-3">
          <PopoverTitle className="text-sm font-semibold">Modelo para Demo Chat</PopoverTitle>
          <PopoverDescription className="text-xs">
            Cambia el modelo de la versión #{latestVersion.version} Latest. Los clientes reales siguen con{' '}
            <strong className="text-text-default">{getAgentModelName(currentModel, options)}</strong>.
          </PopoverDescription>
          {isMasterTest && (
            <p className="m-0 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
              El prompt común prueba <strong>{getAgentModelName(selectedModel, options)}</strong> en Demo Chat para
              todos los subproyectos. Elige otro modelo para probarlo solo en este subproyecto.
            </p>
          )}
        </PopoverHeader>
        <div role="listbox" aria-label="Modelos" className="flex max-h-[400px] flex-col overflow-y-auto p-1.5">
          {options.map((option) => {
            const isSelected = option.id === selectedModel;
            const price = formatAgentModelPrice(option);
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                // Keeps the selected model visible when the list opens.
                ref={isSelected ? (element) => element?.scrollIntoView({ block: 'nearest' }) : undefined}
                disabled={isSaving}
                onClick={() => handleSelect(option.id)}
                className={cn(
                  'flex items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-gray-50 disabled:opacity-60',
                  isSelected && 'bg-indigo-50/60 hover:bg-indigo-50'
                )}
              >
                <Check className={cn('mt-0.5 size-4 shrink-0 text-indigo-600', !isSelected && 'invisible')} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-text-default">
                    {getAgentModelName(option.id, options)}
                    <ModelTags option={option} currentModel={currentModel} currentOption={currentOption} />
                  </span>
                  <span className="text-xs text-text-muted">{option.description}</span>
                  {price && <span className="text-[11px] text-text-subtle">{price} por 1M tokens</span>}
                </span>
              </button>
            );
          })}
        </div>
        {(isSaving || error) && (
          <p
            className={cn(
              'm-0 border-t border-border-subtle px-4 py-2 text-xs',
              error ? 'text-destructive' : 'text-text-muted'
            )}
          >
            {error ?? 'Guardando…'}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ChangeModelPopover;
